/**
 * How a lead's Network fallback hand-off reads on the lead page, its timeline
 * and the dashboard (server migration 094, jobs/partner/network-fallback.job.ts).
 * When nobody on the partner's roster took a lead and the homeowner ticked the
 * separate referral box, FinderlyFix passes it to its matching network, eLocal.
 */
import type { NetworkFallback } from '../api/types';

/** Why a lead was not sent, or why the network did not take it, as the partner reads it. */
const REASON_COPY: Record<string, string> = {
  no_postal_code: 'the homeowner gave no ZIP code',
  no_phone: 'the homeowner gave no usable phone number',
  no_first_name: 'the homeowner gave no first name',
  no_trade_mapping: 'the network has no category for this trade',
  no_consent_record: 'the homeowner’s consent record is missing',
  no_consent_certificate: 'no consent certificate was captured with the request',
  network_unavailable: 'the network is not available right now',
  not_configured: 'the network is not available right now',
  sale_optout: 'the homeowner has opted out of the sale of their information',
  tcpa_revocation: 'the homeowner asked not to be called',
  contractor_accepted: 'one of your contractors took it',
  no_longer_unmatched: 'it went back to your contractors',
  fallback_off: 'the Network fallback was turned off',
  not_eligible: 'it no longer qualified',
  no_buyer: 'no company in the network took it',
  post_unconfirmed: 'we could not confirm the result',
  retries_exhausted: 'the network did not answer',
  invalid_key: 'the network refused the request (a problem on our side)',
  account_inactive: 'the network refused the request (a problem on our side)',
  unmapped_category: 'the network refused the request (a problem on our side)',
  bad_request: 'the network refused the request (a problem on our side)',
};

export function networkFallbackReason(reason: unknown): string | null {
  return typeof reason === 'string' ? (REASON_COPY[reason] ?? null) : null;
}

const NETWORK = 'our matching network (eLocal)';

/**
 * One sentence for the hand-off. Takes the lead API's `network_fallback` or a
 * timeline entry's `detail` (both carry `status`, `reason` and `sent_at`).
 */
export function networkFallbackSentence(nf: Pick<NetworkFallback, 'status'> & { reason?: unknown; sent_at?: unknown }): string {
  const why = networkFallbackReason(nf.reason);
  const sent = typeof nf.sent_at === 'string' && nf.sent_at.length > 0;
  switch (nf.status) {
    case 'queued':
    case 'sending':
      return `Sending to ${NETWORK}…`;
    case 'accepted':
      return `Sent to ${NETWORK} · taken`;
    case 'rejected':
      return `Sent to ${NETWORK} · not taken${why ? `: ${why}` : ''}`;
    case 'error':
      return sent
        ? `Sent to ${NETWORK} · ${why ?? 'we could not confirm the result'}`
        : `Couldn’t send to ${NETWORK}${why ? `: ${why}` : ''}`;
    case 'dry_run':
      return `Sent to ${NETWORK} · test run, nothing was actually sent`;
    case 'skipped':
      return `Not sent to ${NETWORK}${why ? `: ${why}` : ''}`;
    default:
      return `Matching network: ${String(nf.status)}`;
  }
}
